import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, invalidateAuthSession } from '../lib/api';
import { initWebPush, unregisterWebPushToken, resetWebPush } from '../services/webPush';
import { applyUserLanguagePreference, applyLanguage } from '../i18n/languageManager';

const AuthContext = createContext(null);

// Best-effort: register the FCM token for the active user after auth.
// Fully graceful — no UI prompt unless permission is `default`.
function bootstrapPushIfAuthenticated(user) {
  if (!user) return;
  try { initWebPush(); } catch { /* noop */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionRecovering, setSessionRecovering] = useState(false);
  // Monotonic session identity. Bumped on login/logout/account-switch so that a
  // recovery retry or an in-flight /auth/me from a previous session is ignored.
  const sessionGenRef = useRef(0);
  const recoveryTimerRef = useRef(null);

  useEffect(() => {
    // Check for stored user on mount
    const storedUser = localStorage.getItem('snappipit_user');
    const token = localStorage.getItem('snappipit_token');
    
    if (storedUser && token) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      // Render from the last authenticated snapshot immediately. Token
      // validation continues in the background and still logs out an invalid
      // session, but a slow /auth/me no longer blocks the entire application.
      setLoading(false);
      bootstrapPushIfAuthenticated(parsed);
      applyUserLanguagePreference(parsed?.language);
      // Verify token is still valid. On a transient backend outage we keep the
      // cached session and retry in the background; only a CONFIRMED rejection
      // (a real 401 that is NOT a transient refresh failure) logs the user out.
      let verifyAttempt = 0;
      const verifySession = (gen) => {
        authAPI.me()
          .then(response => {
            // Ignore a response that belongs to an earlier session (the user
            // logged out or switched accounts while this call was in flight).
            if (gen !== sessionGenRef.current) return;
            setUser(response.data);
            localStorage.setItem('snappipit_user', JSON.stringify(response.data));
            applyUserLanguagePreference(response.data?.language);
            setSessionRecovering(false);
          })
          .catch((err) => {
            if (gen !== sessionGenRef.current) return;
            const status = err?.response?.status;
            const transient = err?.isTransientAuthFailure || !err?.response || status >= 500;
            if (status === 401 && !transient) {
              setSessionRecovering(false);
              logout();
              return;
            }
            // Transient backend unavailability: preserve the saved session and
            // retry until the backend recovers or the session is truly rejected.
            setSessionRecovering(true);
            verifyAttempt += 1;
            if (verifyAttempt <= 5) {
              recoveryTimerRef.current = setTimeout(
                () => verifySession(gen),
                Math.min(30000, 3000 * verifyAttempt),
              );
            }
          });
      };
      verifySession(sessionGenRef.current);
    } else {
      setLoading(false);
    }
    return () => {
      // Cancel any pending recovery retry when the provider unmounts.
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    };
  }, []);

  const login = useCallback((userData, tokens) => {
    // New session identity — invalidate any in-flight recovery/verify from a
    // previous user so a late /auth/me cannot restore the old account.
    sessionGenRef.current += 1;
    invalidateAuthSession();
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    setSessionRecovering(false);
    localStorage.setItem('snappipit_token', tokens.access_token);
    localStorage.setItem('snappipit_refresh_token', tokens.refresh_token);
    localStorage.setItem('snappipit_user', JSON.stringify(userData));
    setUser(userData);
    bootstrapPushIfAuthenticated(userData);
    applyUserLanguagePreference(userData?.language);
  }, []);

  const logout = useCallback(() => {
    // Invalidate the session identity and stop any pending recovery so a late
    // /auth/me response cannot restore the just-logged-out user.
    sessionGenRef.current += 1;
    invalidateAuthSession();
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    setSessionRecovering(false);
    // Drop the FCM token for the user that's logging out so a different
    // user signing in on the same browser doesn't inherit it.
    try { unregisterWebPushToken(); } catch { /* noop */ }
    resetWebPush();
    localStorage.removeItem('snappipit_token');
    localStorage.removeItem('snappipit_refresh_token');
    localStorage.removeItem('snappipit_user');
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('snappipit_user', JSON.stringify(userData));
  }, []);

  // P9-P1: persist a user's language preference (profile field). Applies the
  // language locally (clamped to active languages) and saves it server-side.
  // No public switcher uses this yet — architecture for P9-P2.
  const updateUserLanguage = useCallback(async (language) => {
    const applied = applyLanguage(language);
    try {
      const res = await authAPI.updatePreferences({ language });
      if (res?.data) {
        setUser(res.data);
        localStorage.setItem('snappipit_user', JSON.stringify(res.data));
      }
    } catch { /* best-effort; local change already applied */ }
    return applied;
  }, []);

  const isCustomer = user?.role === 'customer';
  const isGarageOwner = user?.role === 'garage_owner';
  const isGarageStaff = user?.role === 'garage_staff';
  const isGarage = isGarageOwner || isGarageStaff;
  const isDealerOwner = user?.role === 'dealer_owner';
  const isDealerStaff = user?.role === 'dealer_staff';
  const isDealer = isDealerOwner || isDealerStaff;
  const isProvider = isGarage || isDealer;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const value = {
    user,
    loading,
    sessionRecovering,
    login,
    logout,
    updateUser,
    updateUserLanguage,
    isAuthenticated: !!user,
    isCustomer,
    isGarageOwner,
    isGarageStaff,
    isGarage,
    isDealerOwner,
    isDealerStaff,
    isDealer,
    isProvider,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
